import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, Button, Badge } from '@/components/ui/PageHeader';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { AuthButton } from '@/components/ui/AuthButton';
import { useAuth } from '@/context/AuthContext';
import {
  getBoard,
  listBoardItems,
  deleteBoardItem,
  listBoardComments,
  deleteBoardComment,
  type NostalgiaBoard,
  type BoardItem,
  type BoardComment,
} from '@/api/nostalgia';
import BoardForm from './BoardForm';
import ItemForm from './ItemForm';
import BoardControls from './BoardControls';

export default function BoardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [board, setBoard] = useState<NostalgiaBoard | null>(null);
  const [items, setItems] = useState<BoardItem[]>([]);
  const [comments, setComments] = useState<BoardComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals
  const [showEditBoard, setShowEditBoard] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState<BoardItem | null>(null);
  const [deletingItem, setDeletingItem] = useState<BoardItem | null>(null);
  const [deletingComment, setDeletingComment] = useState<BoardComment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id || !isAuthenticated) return;

    setIsLoading(true);
    try {
      const [boardRes, itemsRes, commentsRes] = await Promise.all([
        getBoard(id),
        listBoardItems(id),
        listBoardComments(id),
      ]);
      setBoard(boardRes.data);
      setItems(itemsRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Failed to fetch board:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id, isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDeleteItem = async () => {
    if (!deletingItem || !id) return;

    setIsDeleting(true);
    try {
      await deleteBoardItem(id, deletingItem.id);
      setDeletingItem(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete item:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!deletingComment || !id) return;

    setIsDeleting(true);
    try {
      await deleteBoardComment(deletingComment.id);
      setDeletingComment(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete comment:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleItemSuccess = () => {
    setShowAddItem(false);
    setEditingItem(null);
    fetchData();
  };

  const handleBoardSuccess = () => {
    setShowEditBoard(false);
    fetchData();
  };

  const itemColumns: Column<BoardItem>[] = [
    {
      key: 'sortOrder',
      header: '#',
      render: (item) => item.sortOrder,
      className: 'w-12',
    },
    {
      key: 'itemType',
      header: 'Type',
      render: (item) => (
        <Badge variant="blue">{item.itemType}</Badge>
      ),
    },
    {
      key: 'content',
      header: 'Content Preview',
      render: (item) => {
        const content = item.content as Record<string, unknown>;
        if (item.itemType === 'text') return String(content.text || '').substring(0, 50) + '...';
        if (item.itemType === 'image') return String(content.caption || content.url || '');
        if (item.itemType === 'quote') return `"${String(content.text || '').substring(0, 40)}..."`;
        if (item.itemType === 'poll') return String(content.question || '');
        return JSON.stringify(content).substring(0, 50) + '...';
      },
    },
    {
      key: 'slotIndex',
      header: 'Slot',
      render: (item) => item.slotIndex ?? '-',
    },
  ];

  const commentColumns: Column<BoardComment>[] = [
    {
      key: 'user',
      header: 'User',
      render: (comment) => comment.user?.name || 'Unknown',
    },
    {
      key: 'content',
      header: 'Comment',
      render: (comment) => comment.content.substring(0, 100) + (comment.content.length > 100 ? '...' : ''),
    },
    {
      key: 'createdAt',
      header: 'Date',
      render: (comment) => new Date(comment.createdAt).toLocaleDateString(),
    },
  ];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold">Board Detail</h1>
            <AuthButton />
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800">Please login to view board details.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!board) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-gray-900">Board not found</h1>
          <Button onClick={() => navigate('/lab/nostalgia/boards')} className="mt-4">
            Back to Boards
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <PageHeader
            title={board.title}
            subtitle={board.description || undefined}
            breadcrumbs={[
              { label: 'Nostalgia', href: '/lab/nostalgia' },
              { label: 'Boards', href: '/lab/nostalgia/boards' },
              { label: board.title },
            ]}
          />
          <AuthButton />
        </div>

        {/* Board Info */}
        <div className="bg-white rounded-lg border p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {board.era && <Badge variant="purple">{board.era.replace('era_', '')}</Badge>}
                {board.theme && <Badge variant="blue">{board.theme.replace('_', ' ')}</Badge>}
                {board.region && <Badge variant="gray">{board.region}</Badge>}
                <Badge variant={board.visibility === 'everyone' ? 'green' : 'yellow'}>
                  {board.visibility}
                </Badge>
              </div>
              <div className="text-sm text-gray-500">
                Layout: {board.layoutMode} • {board.itemCount} items • {board.viewCount} views
              </div>
              {board.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {board.tags.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <Button variant="secondary" onClick={() => setShowEditBoard(true)}>
              Edit Board
            </Button>
          </div>
        </div>

        <div className="mb-6">
          <BoardControls
            boardId={board.id}
            items={items}
            currentLayout={board.layoutMode}
            onChanged={fetchData}
          />
        </div>

        {/* Items Section */}
        <div className="bg-white rounded-lg border mb-6">
          <div className="px-6 py-4 border-b flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">Items ({items.length})</h2>
            <Button size="sm" onClick={() => setShowAddItem(true)}>
              + Add Item
            </Button>
          </div>
          <DataTable
            columns={itemColumns}
            data={items}
            keyField="id"
            emptyMessage="No items yet. Add your first memory!"
            actions={(item) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingItem(item)}
                  className="text-blue-600 hover:text-blue-800 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => setDeletingItem(item)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Delete
                </button>
              </div>
            )}
          />
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg border">
          <div className="px-6 py-4 border-b">
            <h2 className="font-semibold text-gray-900">Comments ({comments.length})</h2>
          </div>
          <DataTable
            columns={commentColumns}
            data={comments}
            keyField="id"
            emptyMessage="No comments yet."
            actions={(comment) => (
              <button
                onClick={() => setDeletingComment(comment)}
                className="text-red-600 hover:text-red-800 text-sm"
              >
                Delete
              </button>
            )}
          />
        </div>

        {/* Edit Board Modal */}
        <Modal
          isOpen={showEditBoard}
          onClose={() => setShowEditBoard(false)}
          title="Edit Board"
          size="lg"
        >
          <BoardForm
            board={board}
            onSuccess={handleBoardSuccess}
            onCancel={() => setShowEditBoard(false)}
          />
        </Modal>

        {/* Add Item Modal */}
        <Modal
          isOpen={showAddItem}
          onClose={() => setShowAddItem(false)}
          title="Add Item"
          size="lg"
        >
          <ItemForm
            boardId={board.id}
            onSuccess={handleItemSuccess}
            onCancel={() => setShowAddItem(false)}
          />
        </Modal>

        {/* Edit Item Modal */}
        <Modal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          title="Edit Item"
          size="lg"
        >
          {editingItem && (
            <ItemForm
              boardId={board.id}
              item={editingItem}
              onSuccess={handleItemSuccess}
              onCancel={() => setEditingItem(null)}
            />
          )}
        </Modal>

        {/* Delete Item Confirmation */}
        <ConfirmDialog
          isOpen={!!deletingItem}
          onClose={() => setDeletingItem(null)}
          onConfirm={handleDeleteItem}
          title="Delete Item"
          message="Are you sure you want to delete this item?"
          confirmText="Delete"
          isLoading={isDeleting}
        />

        {/* Delete Comment Confirmation */}
        <ConfirmDialog
          isOpen={!!deletingComment}
          onClose={() => setDeletingComment(null)}
          onConfirm={handleDeleteComment}
          title="Delete Comment"
          message="Are you sure you want to delete this comment?"
          confirmText="Delete"
          isLoading={isDeleting}
        />
      </div>
    </div>
  );
}
